import { LngLat } from "mapbox-gl";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useMap } from "react-map-gl";
import { useContextMenuStore, useDetailsStore, usePopupStore, usePropertyStore, useSearchStore } from "../..";
import useNavigateToProperty from "../../hooks/useNavigateToProperty";
import useProperty from "../../hooks/useProperty";
import { usePopupShapesOnMap } from "../../hooks/useShapesOnMap";
import DetailsType from "../../interfaces/DetailsType";
import SearchType from "../../interfaces/SearchType";
import { formatDate, formatNumber, formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import PropertyIcon from "../icons/PropertyIcon";
import MapPopupWithFlyTo from "../MapPopupWithFlyTo";
import popupStyles from "../Popup.module.css";
import OnMapSpinner from "../spinners/OnMapSpinner";
import ZoningDetailsModal from "./ZoningDetailsModal";

function PropertyPopup({ longitude, latitude }: IPropertyPopupProps) {
    const map = useMap().map;
    const popupStore = usePopupStore();
    const searchStore = useSearchStore();
    const contextMenuStore = useContextMenuStore();
    const detailsStore = useDetailsStore();
    const propertyStore = usePropertyStore();
    const coordinates = useMemo(() => new LngLat(longitude, latitude), [longitude, latitude]);
    const { property, isError, isLoading, fetchProperty } = useProperty(coordinates);
    const navigateToProperty = useNavigateToProperty();
    const [showZoningDetails, setShowZoningDetails] = useState(false);
    usePopupShapesOnMap(property?.shapes, "PropertyShape");

    useEffect(() => fetchProperty(), [coordinates]);

    useEffect(() => {
        if (!map || !property || (detailsStore.type && detailsStore.type !== DetailsType.PropertyDetails)) return;

        contextMenuStore.setOnAddParcel(coord => propertyStore.addParcelsToProperty(coordinates, [coord], map), true);
        if (property.parcelIds.length > 1) {
            contextMenuStore.setOnRemoveParcel(
                id => propertyStore.removeParcelFromProperty(coordinates, id),
                coord => propertyStore.findParcelAtCoordinate(coordinates, coord),
            );
        }

        return () => {
            contextMenuStore.clearOnAddParcel();
            contextMenuStore.clearOnRemoveParcel();
        };
    }, [contextMenuStore, detailsStore.type, propertyStore, coordinates, map, property]);

    if (!property && !isLoading && !isError)
        return null;

    if (!property && isLoading)
        return <OnMapSpinner />;

    return (
        <>
            {showZoningDetails && property && <ZoningDetailsModal property={property} onClose={() => setShowZoningDetails(false)} />}
            {isLoading && <OnMapSpinner />}
            <MapPopupWithFlyTo
                longitude={Number(longitude)}
                latitude={Number(latitude)}
                closeButton={true}
                closeOnClick={false}
                onClose={() => popupStore.close()}
                anchor="bottom"
            >
                {property && (
                    <>
                        <div className={popupStyles.header}>
                            <PropertyIcon color="white" />
                            <div className={popupStyles.titleClickable} onClick={() => navigateToProperty(longitude, latitude, true)}>
                                {property.address}
                            </div>
                        </div>
                        <div className={popupStyles.table}>
                            <div className={popupStyles.tableItem}>
                                <div>
                                    Zoning{" "}
                                    <span className={commonStyles.link} onClick={() => setShowZoningDetails(true)}>(Details)</span>
                                </div>
                                <div>{property.zoning}</div>
                            </div>
                            <div className={popupStyles.tableItem}>
                                <div>Building Size</div>
                                <div>{!!property.buildingSize && `${formatNumber(property.buildingSize)} Sq-Ft`}</div>
                            </div>
                            <div className={popupStyles.tableItem}>
                                <div>Land Size</div>
                                <div>{!!property.landSize && `${formatNumber(property.landSize)} Acres`}</div>
                            </div>
                        </div>
                        <div className={popupStyles.body}>
                            <div>{property.ownerEntity}</div>
                            <div className={commonStyles.link} onClick={() => searchStore.setSearch(property.ownerAddress!, SearchType.Leads)}>{property.ownerAddress}</div>
                            {(!!property.saleDate || !!property.salePrice) && (
                                <div>
                                    <span>Sale Date / Price: </span>
                                    {!!property.saleDate && formatDate(new Date(property.saleDate))}
                                    {!!property.saleDate && !!property.salePrice && ' - '}
                                    {!!property.salePrice && formatPrice(property.salePrice)}
                                </div>
                            )}
                        </div>
                    </>
                )}
                {!property && isError && (
                    <>
                        <div className={popupStyles.header}>Property Info</div>
                        <div className={popupStyles.body}>No property information found</div>

                    </>
                )}
            </MapPopupWithFlyTo>
        </>
    );
}

export default observer(PropertyPopup);

interface IPropertyPopupProps {
    longitude: number;
    latitude: number;
}
